import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import axiosClient from "./lib/axiosClient";
import { useReportUpload, UPLOAD_BURST_CONCURRENCY } from "./hooks/useReportUpload";
import { useNetwork } from "./hooks/useNetwork";
import {
  useOfflineSync,
  queuedFilesToFileObjects,
  clearOfflineQueueForUser,
  offlineFileStableKey,
} from "./hooks/useOfflineSync";
import {
  submitTechnicianReport,
  createTechnicianReport,
} from "./utils/submitTechnicianReport";
import {
  clearTechnicianFormDraft,
  hasMeaningfulTechnicianDraft,
} from "./lib/technicianDraftStorage";
import {
  UploadJobsProvider,
  UploadSyncFooter,
} from "./context/UploadContext";
import ViewLoader from "./components/ui/ViewLoader";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Modal } from "react-bootstrap";
import AutoDismissToast from "./Toast";
import SubmissionSuccessModal from "./components/modals/SubmissionSuccessModal";
import ReportPreviewModal from "./components/employee/ReportPreviewModal";
import {
  ArrowRight,
  Plus,
  List,
} from "react-bootstrap-icons";
import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import Login from "./components/Login";
import {
  AuthRoutingProvider,
  PublicRoute,
  ProtectedRoute,
} from "./components/routing/AuthRouteGuards";
import RoutingErrorBoundary from "./components/routing/RoutingErrorBoundary";

const AdminPanel = lazy(() => import("./AdminPanel"));
const TechnicianForm = lazy(() => import("./components/employee/TechnicianForm"));
const EmployeeReportsList = lazy(() => import("./components/employee/EmployeeReportsList"));

// Use environment variable or fallback to relative URL for production
// If REACT_APP_BACKEND_URL is set, use it; otherwise use empty string for relative paths
// Ensure it doesn't end with a slash to avoid double slashes
// If it's set to just "api" or "api/", treat it as empty (relative paths)
const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || '';
let backendUrl = rawBackendUrl.endsWith('/') ? rawBackendUrl.slice(0, -1) : rawBackendUrl;

// Normalize: if it's just "api" (common mistake), use empty string for relative paths
if (backendUrl === 'api' || backendUrl === '/api') {
  console.warn('[CONFIG] REACT_APP_BACKEND_URL is set to "api" or "/api". Using relative paths instead.');
  backendUrl = '';
}

// Validate: if it doesn't start with http:// or https:// and is not empty, it's likely a mistake
if (backendUrl && !backendUrl.startsWith('http://') && !backendUrl.startsWith('https://') && !backendUrl.startsWith('/')) {
  console.warn(`[CONFIG] REACT_APP_BACKEND_URL="${backendUrl}" doesn't look like a valid URL. Using relative paths instead.`);
  backendUrl = '';
}

const App = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(null);
  const [role, setRole] = useState("");
  /** true until localStorage + JWT hydration finishes — never render route tree in this limbo. */
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [systemSettings, setSystemSettings] = useState({
    enable_report_workflow: true,
  });
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [address, setAddress] = useState("");
  const [appointmentType, setAppointmentType] = useState("");
  const [routerOption, setRouterOption] = useState("");
  const [routerSerial, setRouterSerial] = useState("");
  const [ontOption, setOntOption] = useState("");
  const [ontSerial, setOntSerial] = useState("");
  const [inesLength, setInesLength] = useState("");
  const [prizakia, setPrizakia] = useState("");
  const [spiralOption, setSpiralOption] = useState("");
  const [spiralMeters, setSpiralMeters] = useState("");
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sr, setSr] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [lockoutEndsAtMs, setLockoutEndsAtMs] = useState(null);
  const [lockoutRemainingSeconds, setLockoutRemainingSeconds] = useState(0);
  const [showSubmissionSuccessModal, setShowSubmissionSuccessModal] = useState(false);
  const [submissionSuccessPendingCount, setSubmissionSuccessPendingCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [reportSaved, setReportSaved] = useState(false); // Track if report was saved
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false); // Show logout confirmation modal
  /** upload_in_progress | pending_offline | unsaved_draft | confirm_default */
  const [logoutConfirmReason, setLogoutConfirmReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // Track if form is submitting/uploading (locks form fields)
  const [formDirty, setFormDirty] = useState(false); // Track if form has unsaved changes
  const [, setShowSuccessPanel] = useState(false); // Control visibility of success panel (setter kept for parity)
  const [employeeView, setEmployeeView] = useState("submit"); // submit | history
  const [myReports, setMyReports] = useState([]);
  const [myReportsLoading, setMyReportsLoading] = useState(false);
  const [myReportsError, setMyReportsError] = useState("");
  /** Client-side list pagination (after full fetch into `myReports`) */
  const [myReportsListPage, setMyReportsListPage] = useState(1);
  const [myReportsPerPage, setMyReportsPerPage] = useState("5");
  const [myReportsTotal, setMyReportsTotal] = useState(0);
  const [myReportsSearch, setMyReportsSearch] = useState("");
  const [myReportsSearchDebounced, setMyReportsSearchDebounced] = useState("");
  const [myReportsStartDate, setMyReportsStartDate] = useState("");
  const [myReportsEndDate, setMyReportsEndDate] = useState("");
  /** Debounced for API only — avoids fetch + loading toggles while native date UI is open */
  const [myReportsStartDateDebounced, setMyReportsStartDateDebounced] = useState("");
  const [myReportsEndDateDebounced, setMyReportsEndDateDebounced] = useState("");
  const [myReportsStatus, setMyReportsStatus] = useState("all");
  const [myReportPreview, setMyReportPreview] = useState(null);
  const [employeeWelcomeName, setEmployeeWelcomeName] = useState(
    () => localStorage.getItem("employeeWelcomeName") || ""
  );
  /** Bump to remount TechnicianForm and clear its local UI state (scanner, draft chip, etc.) after a successful send. */
  const [technicianFormMountKey, setTechnicianFormMountKey] = useState(0);
  /** Files waiting in UploadJobsProvider (not yet finished uploading). */
  const [backgroundUploadPending, setBackgroundUploadPending] = useState(0);
  const enqueueBackgroundUploadRef = useRef(null);
  const clearBackgroundUploadQueueRef = useRef(null);

  const [errors, setErrors] = useState({
    date: '',
    appointmentType: '',
    sr: '',
    address: '',
    routerOption: '',
    routerSerial: '',
    ontOption: '',
    ontSerial: '',
    inesLength: '',
    prizakia: '',
    spiralOption: '',
    spiralMeters: '',
    general: ''
  });

  const handleAppointmentTypeChange = useCallback((value) => {
    setAppointmentType(value);
    if (value !== "ΣΠΙΡΑΛ") {
      setSpiralOption("");
      setSpiralMeters("");
      setErrors((prev) => ({ ...prev, spiralOption: "", spiralMeters: "" }));
    }
  }, []);

  function formatLockoutMessage(seconds) {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    const minutes = Math.floor(s / 60);
    const remainingSecs = s % 60;
    if (minutes > 0) {
      return `❌ Ο λογαριασμός κλειδώθηκε προσωρινά λόγω πολλαπλών αποτυχημένων προσπαθειών. Δοκιμάστε ξανά σε ${minutes} λεπτά${remainingSecs > 0 ? ` και ${remainingSecs} δευτερόλεπτα` : ""}.`;
    }
    return `❌ Ο λογαριασμός κλειδώθηκε προσωρινά λόγω πολλαπλών αποτυχημένων προσπαθειών. Δοκιμάστε ξανά σε ${remainingSecs} δευτερόλεπτα.`;
  }

  useEffect(() => {
    if (!lockoutEndsAtMs) return;

    const tick = () => {
      const remainingMs = lockoutEndsAtMs - Date.now();
      const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
      setLockoutRemainingSeconds(remainingSec);
      setErrors((prev) => ({ ...prev, general: formatLockoutMessage(remainingSec) }));

      if (remainingMs <= 0) {
        setLockoutEndsAtMs(null);
        setLockoutRemainingSeconds(0);
        setShowToast(false);
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockoutEndsAtMs]);

  const reportUpload = useReportUpload({
    backendUrl,
    token: token || '',
    setErrors,
    setShowToast,
  });

  const {
    uploadingFiles,
    hasActiveUploads,
    progressByAttachmentId,
    uploadCancelTokens,
    attachmentStatuses,
    setAttachmentStatuses,
    currentReportId,
    computeGlobalProgress,
    retryAttachmentUpload,
    uploadFileAsync,
    pollAttachmentStatus,
    setUploadSession,
    setCurrentReportId,
    setProgressByAttachmentId,
    setHasActiveUploads,
    setHasActiveProcessing,
    setReportCompleted,
    setCompletedReportIdShown,
    setUploadProgress,
    setUploadStats,
    resetEmployeeUploadState,
    resetForLogout,
    stopPolling,
  } = reportUpload;

  const formRef = useRef();
  const myReportsTopRef = useRef(null);
  const logoutInProgressRef = useRef(false);
  const performLogoutRef = useRef(async () => {});
  const { isOnline } = useNetwork();

  /** After successful submit: scroll window + root into view once layout has committed (mobile-safe). */
  const scrollWorkEntryToTop = useCallback(() => {
    const run = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
      formRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  }, []);

  const technicianDraftUser =
    role === "employee" ? (employeeWelcomeName || username || "").trim() : "";

  const processQueuedReport = useCallback(
    async (item, meta) => {
      const sidecar = (await meta.readProgress()) || {};
      const allFiles = queuedFilesToFileObjects(item.files);
      const uploadedSet = new Set(sidecar.uploadedFileKeys || []);

      let reportId;

      if (allFiles.length === 0) {
        ({ reportId } = await submitTechnicianReport({
          backendUrl,
          token,
          fields: item.fields,
          files: [],
          uploadFileAsync,
          onAfterUpload: undefined,
        }));
      } else {
        reportId = sidecar.serverReportId;
        if (!reportId) {
          reportId = await createTechnicianReport({
            backendUrl,
            token,
            fields: item.fields,
            hasAttachments: true,
          });
          await meta.writeProgress({
            serverReportId: reportId,
            uploadedFileKeys: [...uploadedSet],
          });
        }

        const pendingFiles = allFiles.filter(
          (f) => !uploadedSet.has(offlineFileStableKey(f))
        );

        for (let i = 0; i < pendingFiles.length; i += UPLOAD_BURST_CONCURRENCY) {
          const wave = pendingFiles.slice(i, i + UPLOAD_BURST_CONCURRENCY);
          await Promise.all(
            wave.map(async (file) => {
              await uploadFileAsync(file, reportId);
              uploadedSet.add(offlineFileStableKey(file));
            })
          );
          await meta.writeProgress({
            serverReportId: reportId,
            uploadedFileKeys: [...uploadedSet],
          });
        }
      }

      if (allFiles.length > 0) {
        pollAttachmentStatus(reportId).catch((err) => {
          console.error("Background polling (queued report):", err);
        });
      }
      setShowToast(false);
      setErrors((prev) => ({ ...prev, general: "" }));
      setSubmissionSuccessPendingCount(allFiles.length);
      setShowSubmissionSuccessModal(true);
      stopPolling();
      setProgressByAttachmentId({});
      setUploadSession(null);
      setHasActiveUploads(false);
      setHasActiveProcessing(false);
      setCurrentReportId(null);
      setAttachmentStatuses({});
      setEmployeeView("submit");
      scrollWorkEntryToTop();
      setTechnicianFormMountKey((k) => k + 1);
    },
    [
      token,
      uploadFileAsync,
      pollAttachmentStatus,
      setProgressByAttachmentId,
      setUploadSession,
      setCurrentReportId,
      setHasActiveUploads,
      setHasActiveProcessing,
      setAttachmentStatuses,
      setErrors,
      setShowToast,
      stopPolling,
      scrollWorkEntryToTop,
      setSubmissionSuccessPendingCount,
      setShowSubmissionSuccessModal,
    ]
  );

  const { enqueue, queueLength } = useOfflineSync({
    isOnline,
    enabled: Boolean(token && role === "employee" && technicianDraftUser),
    queueUserKey: technicianDraftUser || null,
    processQueuedReport,
  });

  /** Loads every server page (limit capped at 50 by API) into memory for client-side pagination. */
  const fetchMyReports = useCallback(async () => {
    if (!token) return;
    const apiLimit = 50;
    setMyReportsLoading(true);
    setMyReportsError("");
    try {
      const qs = new URLSearchParams();
      const qSearch = (myReportsSearchDebounced || "").trim();
      if (qSearch) qs.set("search", qSearch);
      if (myReportsStartDateDebounced) qs.set("startDate", myReportsStartDateDebounced);
      if (myReportsEndDateDebounced) qs.set("endDate", myReportsEndDateDebounced);
      if (myReportsStatus && myReportsStatus !== "all") qs.set("status", myReportsStatus);
      const filterSuffix = qs.toString() ? `&${qs.toString()}` : "";

      const all = [];
      let serverPage = 1;
      let serverTotalPages = 1;
      let totalFromApi = 0;
      const maxServerPages = 200;
      while (serverPage <= serverTotalPages && serverPage <= maxServerPages) {
        const response = await axiosClient.get(
          `${backendUrl}/api/my-reports?page=${serverPage}&limit=${apiLimit}${filterSuffix}`
        );
        const chunk = Array.isArray(response.data?.data) ? response.data.data : [];
        const pagination = response.data?.pagination || {};
        serverTotalPages = Math.max(1, pagination.totalPages || 1);
        if (typeof pagination.total === "number") {
          totalFromApi = pagination.total;
        }
        if (chunk.length === 0) break;
        all.push(...chunk);
        serverPage += 1;
      }
      setMyReports(all);
      setMyReportsTotal(totalFromApi || all.length);
    } catch (error) {
      console.error("Error fetching my reports:", error);
      setMyReportsError("Δεν ήταν δυνατή η φόρτωση των αναφορών αυτή τη στιγμή.");
    } finally {
      setMyReportsLoading(false);
    }
  }, [
    token,
    backendUrl,
    myReportsSearchDebounced,
    myReportsStartDateDebounced,
    myReportsEndDateDebounced,
    myReportsStatus,
  ]);

  const clearMyReportFilters = useCallback(() => {
    setMyReportsSearch("");
    setMyReportsSearchDebounced("");
    setMyReportsStartDate("");
    setMyReportsEndDate("");
    setMyReportsStartDateDebounced("");
    setMyReportsEndDateDebounced("");
    setMyReportsStatus("all");
  }, []);

  const scrollToFirstError = () => {
    window.setTimeout(() => {
      const firstErrorElement = document.querySelector(
        ".is-invalid, [aria-invalid='true']"
      );
      if (!firstErrorElement) return;
      firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      if (
        firstErrorElement.matches?.("input, textarea, select")
      ) {
        try {
          firstErrorElement.focus({ preventScroll: true });
        } catch {
          try {
            firstErrorElement.focus();
          } catch {
            /* ignore */
          }
        }
      }
    }, 0);
  };

  const validateForm = () => {
    const newErrors = {
      date: !date ? 'Υποχρεωτικό Πεδίο' : '',
      appointmentType: !appointmentType ? 'Υποχρεωτικό Πεδίο' : '',
      sr: !sr ? 'Υποχρεωτικό Πεδίο' : '',
      address: !address ? 'Υποχρεωτικό Πεδίο' : '',
      routerOption: !routerOption ? 'Υποχρεωτικό Πεδίο' : '',
      routerSerial: routerOption === 'yes' && !routerSerial ? 'Υποχρεωτικό Πεδίο' : '',
      ontOption: !ontOption ? 'Υποχρεωτικό Πεδίο' : '',
      ontSerial: ontOption === 'yes' && !ontSerial ? 'Υποχρεωτικό Πεδίο' : '',
      inesLength: !inesLength ? 'Υποχρεωτικό Πεδίο' : '',
      prizakia: !prizakia ? 'Υποχρεωτικό Πεδίο' : '',
      spiralOption:
        appointmentType === "ΣΠΙΡΑΛ" && !spiralOption ? 'Υποχρεωτικό Πεδίο' : '',
      spiralMeters:
        appointmentType === "ΣΠΙΡΑΛ" && spiralOption === "yes" && !spiralMeters
          ? 'Υποχρεωτικό Πεδίο'
          : '',
      general: ''
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  /** Used by TechnicianForm preview step — same rules as submit, without starting upload. */
  /** Validation only; TechnicianForm runs deferred scroll/focus after React paints `.is-invalid`. */
  const validateWorkEntryStep = () => {
    if (!validateForm()) {
      return false;
    }
    const selectedDate = new Date(date);
    const currentDate = new Date();
    if (selectedDate > currentDate) {
      setErrors((prev) => ({
        ...prev,
        date: 'Η ημερομηνία δεν μπορεί να είναι στο μέλλον',
      }));
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length === 0) return;
    processFiles(newFiles);
  };

  const processFiles = (newFiles) => {
    const allowedTypes = [
      "image/jpeg", "image/png", "image/gif",
      "video/mp4", "video/mpeg", "video/quicktime"
    ];

    const invalidFiles = newFiles.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setErrors(prev => ({ ...prev, general: `Μη υποστηριζόμενοι τύποι αρχείων` }));
      setShowToast(true);
      return;
    }

    // Check file sizes
    const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB max per file
    const MAX_TOTAL_SIZE = 1000 * 1024 * 1024; // 1GB max total
    
    const oversizedFiles = newFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(", ");
      setErrors(prev => ({ 
        ...prev, 
        general: `Υπερβολικά μεγάλα αρχεία (μέγιστο 500MB): ${fileNames}` 
      }));
      setShowToast(true);
      return;
    }

    // Check total size including existing attachments
    const currentTotalSize = attachments.reduce((sum, file) => sum + file.size, 0);
    const newTotalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
    if (currentTotalSize + newTotalSize > MAX_TOTAL_SIZE) {
      setErrors(prev => ({ 
        ...prev, 
        general: `Το συνολικό μέγεθος αρχείων υπερβαίνει το όριο 1GB. Μειώστε το μέγεθος ή ανεβάστε λιγότερα αρχεία.` 
      }));
      setShowToast(true);
      return;
    }

    // Warn about large video files
    const largeVideos = newFiles.filter(file => 
      file.type.startsWith("video") && file.size > 50 * 1024 * 1024 // > 50MB
    );
    if (largeVideos.length > 0) {
      const totalSizeMB = (currentTotalSize + newTotalSize) / (1024 * 1024);
      console.log(`Warning: Large video file(s) detected. Total upload size: ${totalSizeMB.toFixed(2)}MB`);
    }

    setAttachments(prev => [...prev, ...newFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const newFiles = Array.from(e.dataTransfer.files);
    if (newFiles.length > 0) {
      processFiles(newFiles);
    }
  };

  const handleRemoveFile = (index) => {
    if (isSubmitting) return; // Prevent removal during submission
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Clear form state on login (start fresh)
  useEffect(() => {
    if (token && role === 'employee') {
      // User just logged in - clear any form state
      setSr("");
      setAddress("");
      setAppointmentType("");
      setRouterOption("");
      setRouterSerial("");
      setOntOption("");
      setOntSerial("");
      setSpiralOption("");
      setSpiralMeters("");
      setInesLength("");
      setPrizakia("");
      setNotes("");
      setAttachments([]);
      setFormDirty(false);
      resetEmployeeUploadState();
      setReportSaved(false);
      setEmployeeView("submit");
      setMyReports([]);
      setMyReportsError("");
      setMyReportsLoading(false);
      setMyReportsListPage(1);
      setMyReportsPerPage("5");
      setMyReportsTotal(0);
      setMyReportsSearch("");
      setMyReportsSearchDebounced("");
      setMyReportsStartDate("");
      setMyReportsEndDate("");
      setMyReportsStartDateDebounced("");
      setMyReportsEndDateDebounced("");
      setErrors({
        date: '',
        appointmentType: '',
        sr: '',
        address: '',
        routerOption: '',
        routerSerial: '',
        ontOption: '',
        ontSerial: '',
        inesLength: '',
        prizakia: '',
        spiralOption: '',
        spiralMeters: '',
        notes: '',
        general: ''
      });
    }
  }, [token, role, resetEmployeeUploadState]);

  useEffect(() => {
    if (token && role === "employee" && employeeView === "history") {
      fetchMyReports();
    }
  }, [token, role, employeeView, fetchMyReports]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setMyReportsSearchDebounced(myReportsSearch);
    }, 400);
    return () => window.clearTimeout(t);
  }, [myReportsSearch]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setMyReportsStartDateDebounced(myReportsStartDate);
      setMyReportsEndDateDebounced(myReportsEndDate);
    }, 500);
    return () => window.clearTimeout(t);
  }, [myReportsStartDate, myReportsEndDate]);

  useEffect(() => {
    setMyReportsListPage(1);
  }, [
    myReportsSearchDebounced,
    myReportsStartDateDebounced,
    myReportsEndDateDebounced,
  ]);

  useEffect(() => {
    const n = myReports.length;
    const per =
      myReportsPerPage === "all" || n === 0
        ? 1
        : Math.max(1, parseInt(myReportsPerPage, 10) || 5);
    const pages =
      myReportsPerPage === "all" || n === 0 ? 1 : Math.max(1, Math.ceil(n / per));
    setMyReportsListPage((p) => (p > pages ? pages : p));
  }, [myReports.length, myReportsPerPage]);

  // Reset login form when user logs out (token becomes null)
  useEffect(() => {
    if (!token) {
      // User is logged out - explicitly clear login form
      setUsername("");
      setPassword("");
      setErrors(prev => ({ ...prev, general: '' }));
    }
  }, [token]);

  // Explicitly reset login form on component mount (when not logged in)
  // This prevents any stale values from previous sessions or browser cache
  useEffect(() => {
    // Check if we're showing login form (no active in-memory auth state)
    if (!token && !role) {
      // Explicitly reset login form fields to empty
      setUsername("");
      setPassword("");
      setErrors(prev => ({ ...prev, general: '' }));
    }
    // Intentionally mount-only: avoid re-running when token/role hydrate from localStorage
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on component mount

  // Track form dirty state - mark as dirty when any field changes
  useEffect(() => {
    if (!token || role !== 'employee') return;
    
    const hasFormData = 
      sr || address || appointmentType || 
      routerOption || routerSerial || ontOption || ontSerial ||
      spiralOption || spiralMeters || inesLength || prizakia || 
      notes || attachments.length > 0;
    
    setFormDirty(hasFormData && !reportSaved);
  }, [sr, address, appointmentType, routerOption, routerSerial, ontOption, ontSerial, 
      spiralOption, spiralMeters, inesLength, prizakia, notes, attachments, 
      reportSaved, token, role]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      // Try /api/login first (for production with Nginx), fallback to /login
      const loginUrl = `${backendUrl}/api/login`;
      const response = await axiosClient.post(loginUrl, { username, password }).catch(async (err) => {
        // If /api/login fails, try /login (for backward compatibility)
        if (err.response?.status === 404 || err.response?.status === 405) {
          return await axiosClient.post(`${backendUrl}/login`, { username, password });
        }
        throw err;
      });
      setToken("cookie-authenticated");
      setRole(response.data.role);
      localStorage.setItem("role", response.data.role);
      if (response.data.role === "employee") {
        const welcome = username.trim();
        localStorage.setItem("employeeWelcomeName", welcome);
        setEmployeeWelcomeName(welcome);
      } else {
        localStorage.removeItem("employeeWelcomeName");
        setEmployeeWelcomeName("");
      }
      // Clear lockout countdown when login succeeds.
      setLockoutEndsAtMs(null);
      setLockoutRemainingSeconds(0);
      setErrors(prev => ({ ...prev, general: "✅ Επιτυχής σύνδεση!" }));
      setShowToast(true);
      navigate(response.data.role === "admin" ? "/admin" : "/", { replace: true });
    } catch (error) {
      console.error("Login failed:", error);
      const status = error?.response?.status;
      const respData = error?.response?.data || {};
      const backendErrorCode = String(respData?.error || "").toUpperCase();
      const backendMessage = String(respData?.message || "").trim();

      if (status === 429 && backendErrorCode === "IP_LOCKED") {
        setLockoutEndsAtMs(null);
        setLockoutRemainingSeconds(0);
        setErrors((prev) => ({
          ...prev,
          general: `❌ ${backendMessage || "Πάρα πολλές προσπάθειες από αυτή τη διεύθυνση IP. Δοκιμάστε ξανά αργότερα."}`,
        }));
        setShowToast(true);
        return;
      }

      if (status === 429 && backendErrorCode === "ACCOUNT_LOCKED") {
        const retryAfterSeconds =
          typeof respData?.retry_after_seconds === "number"
            ? respData.retry_after_seconds
            : 15 * 60;

        const lockedUntil = respData?.locked_until;
        const lockedUntilMs = lockedUntil ? new Date(lockedUntil).getTime() : null;
        const endsAtMs =
          lockedUntilMs && Number.isFinite(lockedUntilMs)
            ? lockedUntilMs
            : Date.now() + Math.max(0, retryAfterSeconds) * 1000;

        setLockoutEndsAtMs(endsAtMs);
        const remainingSec = Math.max(0, Math.floor((endsAtMs - Date.now()) / 1000));
        setLockoutRemainingSeconds(remainingSec);
        setErrors((prev) => ({ ...prev, general: formatLockoutMessage(remainingSec) }));
        setShowToast(true);
        return;
      }

      // Standard login failure message (e.g. 400/401 invalid credentials).
      setLockoutEndsAtMs(null);
      setLockoutRemainingSeconds(0);
      setErrors((prev) => ({ ...prev, general: "❌ Αποτυχία σύνδεσης. Ελέγξτε τα στοιχεία σας." }));
      setShowToast(true);
    }
  };

  // Clear form fields helper function
  const clearFormFields = () => {
    setDate(new Date().toISOString().split("T")[0]);
    setSr("");
    setAddress("");
    setAppointmentType("");
    setRouterOption("");
    setRouterSerial("");
    setOntOption("");
    setOntSerial("");
    setSpiralOption("");
    setSpiralMeters("");
    setInesLength("");
    setPrizakia("");
    setNotes("");
    setAttachments([]);
    // Note: progressByAttachmentId is NOT cleared here - it's cleared separately after delay
    setErrors({
      date: '',
      appointmentType: '',
      sr: '',
      address: '',
      routerOption: '',
      routerSerial: '',
      ontOption: '',
      ontSerial: '',
      inesLength: '',
      prizakia: '',
      spiralOption: '',
      spiralMeters: '',
      notes: '',
      general: ''
    });
  };

  const performWorkEntrySubmit = async () => {
    // Prevent double-click/double-submit
    if (isSubmitting) {
      return;
    }

    if (!validateForm()) {
      scrollToFirstError();
      return;
    }

    const selectedDate = new Date(date);
    const currentDate = new Date();
    if (selectedDate > currentDate) {
      setErrors((prev) => ({
        ...prev,
        date: 'Η ημερομηνία δεν μπορεί να είναι στο μέλλον',
      }));
      scrollToFirstError();
      return;
    }

    if (!isOnline) {
      setIsSubmitting(true);
      try {
        const fields = {
          date,
          sr,
          address,
          appointmentType,
          routerOption,
          routerSerial,
          ontOption,
          ontSerial,
          spiralOption,
          spiralMeters,
          inesLength,
          prizakia,
          notes,
        };
        await enqueue({ fields, files: [...attachments] });
        clearTechnicianFormDraft(technicianDraftUser);
        clearFormFields();
        setFormDirty(false);
        setErrors((prev) => ({
          ...prev,
          general:
            "Εκτός σύνδεσης. Η αναφορά αποθηκεύτηκε και θα σταλεί αυτόματα όταν συνδεθείτε.",
        }));
        setShowToast(true);
        setEmployeeView("submit");
        scrollWorkEntryToTop();
        setTechnicianFormMountKey((k) => k + 1);
      } catch (err) {
        console.error("Offline enqueue failed:", err);
        setErrors((prev) => ({
          ...prev,
          general:
            err?.code === "OFFLINE_QUOTA_EXCEEDED"
              ? err.message
              : "❌ Δεν ήταν δυνατή η αποθήκευση της αναφοράς εκτός σύνδεσης. Δοκιμάστε ξανά.",
        }));
        setShowToast(true);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Reset flags for new submission (online)
    setReportSaved(false);
    setReportCompleted(false); // Reset only when starting a new report
    setHasActiveProcessing(false); // Reset processing flag for new submission
    setShowSuccessPanel(false); // Reset success panel visibility
    setCompletedReportIdShown(null); // Reset so new report can show success message

    const filesToUpload = attachments.length > 0 ? [...attachments] : [];

    setIsSubmitting(true); // Lock form only while creating the report row (metadata)

    try {
      const fields = {
        date,
        sr,
        address,
        appointmentType,
        routerOption,
        routerSerial,
        ontOption,
        ontSerial,
        spiralOption,
        spiralMeters,
        inesLength,
        prizakia,
        notes,
      };

      const reportId = await createTechnicianReport({
        backendUrl,
        token,
        fields,
        hasAttachments: filesToUpload.length > 0,
      });

      if (filesToUpload.length > 0) {
        enqueueBackgroundUploadRef.current?.({
          reportId,
          files: filesToUpload,
        });
      }

      setReportSaved(true);

      stopPolling();
      setCurrentReportId(null);

      clearTechnicianFormDraft(technicianDraftUser);
      clearFormFields();
      setFormDirty(false);
      setIsSubmitting(false);
      setEmployeeView("submit");

      setShowToast(false);
      setErrors((prev) => ({ ...prev, general: "" }));
      setSubmissionSuccessPendingCount(filesToUpload.length);
      setShowSubmissionSuccessModal(true);

      scrollWorkEntryToTop();
      setTechnicianFormMountKey((k) => k + 1);

      setTimeout(() => {
        setReportSaved(false);
      }, 5000);
    } catch (error) {
      let errorMessage = "❌ Σφάλμα υποβολής αναφοράς";
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = "⏱️ Λήξη χρονικού ορίου μεταφόρτωσης — τα αρχεία μπορεί να είναι πολύ μεγάλα. Δοκιμάστε μικρότερα αρχεία ή ελέγξτε τη σύνδεσή σας.";
      } else if (error.response?.status === 404) {
        errorMessage = "❌ Δεν βρέθηκε το endpoint αναφοράς. Επικοινωνήστε με την υποστήριξη.";
      } else if (error.response?.status === 403) {
        errorMessage = "❌ Δεν επιτρέπεται η πρόσβαση. Ελέγξτε τα δικαιώματά σας.";
      } else if (error.response?.status === 400) {
        errorMessage = `❌ ${error.response.data?.error || "Μη έγκυρο αίτημα. Ελέγξτε όλα τα υποχρεωτικά πεδία."}`;
      } else if (error.response?.data?.error) {
        errorMessage = `❌ ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage = `❌ ${error.message}`;
      }
      
      console.error("Report submission error:", error);
      setErrors(prev => ({
        ...prev,
        general: errorMessage
      }));
      setShowToast(true);
      
      // Stop polling on error (submit failed)
      stopPolling();
    } finally {
      // Always clear isSubmitting in finally block to ensure UI is never stuck
      // This runs whether submit succeeds or fails
      // Note: On success, isSubmitting is already cleared in the try block,
      // but we clear it here too to ensure it's always cleared
      setIsSubmitting(false);
      
      // Clear progress stats
      setUploadProgress(0);
      setUploadStats({
        loaded: 0,
        total: 0,
        startTime: null,
        speed: 0,
        timeRemaining: 0
      });
      
      // Note: Don't stop polling intervals in finally - polling is optional and non-blocking
      // On success, polling continues in background for informational purposes
      // On error, polling is stopped in catch block above
    }
  };

  const handleLogout = () => {
    if (isSubmitting) {
      return;
    }

    if (role !== "employee") {
      performLogout();
      return;
    }

    const hasProgress = Object.keys(progressByAttachmentId).length > 0;
    const globalProgress = computeGlobalProgress();
    const stillUploading = hasProgress && globalProgress.percent < 100;
    const hasActiveNetworkUploads =
      uploadingFiles.size > 0 ||
      stillUploading ||
      backgroundUploadPending > 0;

    if (hasActiveNetworkUploads) {
      setLogoutConfirmReason("upload_in_progress");
      setShowLogoutConfirm(true);
      return;
    }

    if (queueLength > 0) {
      setLogoutConfirmReason("pending_offline");
      setShowLogoutConfirm(true);
      return;
    }

    const hasPersistedDraft = hasMeaningfulTechnicianDraft(technicianDraftUser);
    const hasInMemoryDraft = formDirty && !reportSaved;
    if (hasPersistedDraft || hasInMemoryDraft) {
      setLogoutConfirmReason("unsaved_draft");
      setShowLogoutConfirm(true);
      return;
    }

    setLogoutConfirmReason("confirm_default");
    setShowLogoutConfirm(true);
  };

  const performLogout = async (options = {}) => {
    const reason = options.reason || "user";

    if (logoutInProgressRef.current) {
      return;
    }
    logoutInProgressRef.current = true;

    const queueUserForClear = (employeeWelcomeName || username || "").trim();

    try {
      try {
        clearBackgroundUploadQueueRef.current?.();
      } catch (e) {
        console.warn("clearBackgroundUploadQueue:", e);
      }

      Object.values(uploadCancelTokens).forEach((cancelToken) => {
        try {
          cancelToken.cancel("Upload cancelled by user logout");
        } catch (error) {
          console.log("Error cancelling upload:", error);
        }
      });

      resetForLogout();

      if (token && reason !== "session_expired") {
        try {
          await axiosClient
            .post(
              `${backendUrl}/api/logout`,
              {}
            )
            .catch(() => {});
        } catch (error) {
          // Ignore errors - logout should proceed
        }
      }

      clearFormFields();
      setFormDirty(false);
      setUsername("");
      setPassword("");
      setToken(null);
      setRole("");
      setReportSaved(false);
      setShowLogoutConfirm(false);
      setShowSuccessPanel(false);
      setErrors({
        date: "",
        appointmentType: "",
        sr: "",
        address: "",
        routerOption: "",
        routerSerial: "",
        ontOption: "",
        ontSerial: "",
        inesLength: "",
        prizakia: "",
        spiralOption: "",
        spiralMeters: "",
        notes: "",
        general: "",
      });

      clearTechnicianFormDraft(queueUserForClear);
      await clearOfflineQueueForUser(queueUserForClear);

      localStorage.removeItem("role");
      localStorage.removeItem("employeeWelcomeName");

      sessionStorage.clear();
      if (reason === "session_expired") {
        sessionStorage.setItem(
          "session_expired_notice",
          options.sessionNotice || "Η συνεδρία σας έληξε. Παρακαλώ συνδεθείτε ξανά."
        );
      }

      navigate("/login", { replace: true });
    } finally {
      logoutInProgressRef.current = false;
    }
  };

  performLogoutRef.current = performLogout;

  useEffect(() => {
    const onAuthExpired = (e) => {
      performLogoutRef.current({
        reason: "session_expired",
        sessionNotice: e.detail?.sessionNotice,
      });
    };
    window.addEventListener("auth-token-expired", onAuthExpired);
    return () => window.removeEventListener("auth-token-expired", onAuthExpired);
  }, []);

  useEffect(() => {
    const savedRole = localStorage.getItem("role") || "";
    if (!savedRole) {
      setIsAuthLoading(false);
      return;
    }
    setToken("cookie-authenticated");
    setRole(savedRole);
    setIsAuthLoading(false);
  }, []);

  useEffect(() => {
    const msg = sessionStorage.getItem("session_expired_notice");
    if (msg) {
      sessionStorage.removeItem("session_expired_notice");
      setErrors((prev) => ({ ...prev, general: msg }));
      setShowToast(true);
    }
  }, []);

  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        const res = await axiosClient.get(`${backendUrl}/api/settings`);
        const s = res?.data || {};
        setSystemSettings((prev) => ({
          ...prev,
          ...s,
        }));
      } catch (err) {
        console.error("Failed to load system settings:", err);
      }
    };
    loadSystemSettings();
  }, []);

  const myReportsListSlice = useMemo(() => {
    const reportCount = myReports.length;
    const perNum =
      myReportsPerPage === "all" || reportCount === 0
        ? reportCount
        : Math.max(1, parseInt(myReportsPerPage, 10) || 5);
    const clientTotalPages =
      myReportsPerPage === "all" || reportCount === 0
        ? 1
        : Math.max(1, Math.ceil(reportCount / perNum));
    const listPage = Math.min(
      Math.max(1, myReportsListPage),
      clientTotalPages
    );
    const sliceStart =
      myReportsPerPage === "all" || reportCount === 0
        ? 0
        : (listPage - 1) * perNum;
    const sliceEnd =
      myReportsPerPage === "all"
        ? reportCount
        : Math.min(sliceStart + perNum, reportCount);
    const paginatedReports = myReports.slice(sliceStart, sliceEnd);
    const rangeLabel =
      reportCount === 0
        ? "Εμφάνιση 0 από 0"
        : `Εμφάνιση ${sliceStart + 1}-${sliceEnd} από ${reportCount}`;
    const navDisabled =
      myReportsLoading || reportCount === 0 || myReportsPerPage === "all";
    return {
      reportCount,
      paginatedReports,
      rangeLabel,
      navDisabled,
      listPage,
      clientTotalPages,
    };
  }, [myReports, myReportsPerPage, myReportsListPage, myReportsLoading]);

  return (
    <UploadJobsProvider
      uploadFileAsync={uploadFileAsync}
      pollAttachmentStatus={pollAttachmentStatus}
      setErrors={setErrors}
      setShowToast={setShowToast}
      onPendingCountChange={setBackgroundUploadPending}
      onRegisterClearQueue={(fn) => {
        clearBackgroundUploadQueueRef.current = fn;
      }}
      onRegisterEnqueue={(fn) => {
        enqueueBackgroundUploadRef.current = fn;
      }}
    >
    <div ref={formRef}>
      {isAuthLoading ? (
        <ViewLoader fullViewport />
      ) : (
        <AuthRoutingProvider value={{ token, role }}>
          <RoutingErrorBoundary>
            <Routes>
              <Route element={<PublicRoute />}>
                <Route
                  path="/login"
                  element={
                    <Login
                      username={username}
                      password={password}
                      onUsernameChange={setUsername}
                      onPasswordChange={setPassword}
                      onSubmit={handleLogin}
                    />
                  }
                />
              </Route>
              <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
                <Route
                  path="/admin/*"
                  element={
                    <Suspense fallback={<ViewLoader fullViewport />}>
                      <AdminPanel
                        token={token}
                        onLogout={handleLogout}
                        systemSettings={systemSettings}
                        onSystemSettingsChange={setSystemSettings}
                      />
                    </Suspense>
                  }
                />
              </Route>
              <Route element={<ProtectedRoute allowedRoles={["employee"]} />}>
                <Route
                  path="/"
                  element={
                <div className="work-entry-container">
          <header className="work-entry-sticky-shell work-entry-sticky-shell--glass">
            <div className="work-entry-sticky-shell__inner">
              <div className="work-entry-header work-entry-header--feed work-entry-header--sticky-bar work-entry-header--employee-top">
                <div className="work-entry-header-primary">
                  <h1 className="work-entry-title">Καταχώρηση Εργασίας</h1>
                  <p className="work-entry-subtitle">
                    {employeeWelcomeName
                      ? `Γεια σου, ${employeeWelcomeName}.`
                      : "Συμπληρώστε τα στοιχεία της εργασίας"}
                  </p>
                </div>
                <button
                  type="button"
                  className="exit-button-header exit-button-header--toolbar"
                  onClick={handleLogout}
                  disabled={isSubmitting}
                  title={isSubmitting ? "Παρακαλώ περιμένετε..." : "Αποσύνδεση"}
                  aria-label={isSubmitting ? "Παρακαλώ περιμένετε" : "Αποσύνδεση · Έξοδος"}
                > 
                  <span className="exit-button-header__label">Έξοδος</span> 
                  <ArrowRight className="exit-button-header__icon" aria-hidden />
                </button>
              </div>
              <div className="employee-view-toggle employee-view-toggle--sticky-pill" role="tablist" aria-label="Προβολή εργασίας">
                <button
                  type="button"
                  role="tab"
                  aria-selected={employeeView === "submit"}
                  className={`employee-view-btn ${employeeView === "submit" ? "active" : ""}`}
                  onClick={() => {
                    setEmployeeView("submit");
                    setMyReportPreview(null);
                    setMyReportsListPage(1);
                  }}
                >
                  <Plus className="employee-view-btn__icon" aria-hidden />
                  Νέα Καταχώρηση
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={employeeView === "history"}
                  className={`employee-view-btn ${employeeView === "history" ? "active" : ""}`}
                  onClick={() => {
                    setMyReportPreview(null);
                    setMyReportsListPage(1);
                    setEmployeeView("history");
                  }}
                >
                  <List className="employee-view-btn__icon" aria-hidden />
                  Οι Αναφορές μου
                </button>
              </div>
            </div>
          </header>
          <div className="work-entry-content-wrapper work-entry-content-wrapper--feed">
            <Suspense fallback={<ViewLoader />}>
              {employeeView === "history" ? (
                <>
                  <EmployeeReportsList
                    listTopRef={myReportsTopRef}
                    loading={myReportsLoading}
                    error={myReportsError}
                    totalCount={myReportsTotal}
                    onRefresh={fetchMyReports}
                    filterSearch={myReportsSearch}
                    onFilterSearchChange={setMyReportsSearch}
                    filterStartDate={myReportsStartDate}
                    onFilterStartDateChange={setMyReportsStartDate}
                    filterEndDate={myReportsEndDate}
                    onFilterEndDateChange={setMyReportsEndDate}
                    filterStatus={myReportsStatus}
                    onFilterStatusChange={setMyReportsStatus}
                    onClearFilters={clearMyReportFilters}
                    perPage={myReportsPerPage}
                    onPerPageChange={(value) => {
                      setMyReportsPerPage(value);
                      setMyReportsListPage(1);
                    }}
                    slice={myReportsListSlice}
                    onPageChange={setMyReportsListPage}
                    onOpenReportPreview={(r) => setMyReportPreview(r)}
                    systemSettings={systemSettings}
                  />
                  <ReportPreviewModal
                    report={myReportPreview}
                    isOpen={Boolean(myReportPreview)}
                    onClose={() => setMyReportPreview(null)}
                    backendUrl={backendUrl}
                    token={token}
                    employeeDisplayName={employeeWelcomeName}
                  />
                </>
              ) : (
                <TechnicianForm
                  key={technicianFormMountKey}
                  draftUsername={technicianDraftUser}
                  isOnline={isOnline}
                  backendUrl={backendUrl}
                  pendingOfflineReports={queueLength}
                  date={date}
                  setDate={setDate}
                  sr={sr}
                  setSr={setSr}
                  prizakia={prizakia}
                  setPrizakia={setPrizakia}
                  appointmentType={appointmentType}
                  setAppointmentType={handleAppointmentTypeChange}
                  address={address}
                  setAddress={setAddress}
                  routerOption={routerOption}
                  setRouterOption={setRouterOption}
                  routerSerial={routerSerial}
                  setRouterSerial={setRouterSerial}
                  ontOption={ontOption}
                  setOntOption={setOntOption}
                  ontSerial={ontSerial}
                  setOntSerial={setOntSerial}
                  inesLength={inesLength}
                  setInesLength={setInesLength}
                  spiralOption={spiralOption}
                  setSpiralOption={setSpiralOption}
                  spiralMeters={spiralMeters}
                  setSpiralMeters={setSpiralMeters}
                  notes={notes}
                  setNotes={setNotes}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  validateWorkEntryStep={validateWorkEntryStep}
                  onFinalWorkEntrySubmit={performWorkEntrySubmit}
                  onLogout={handleLogout}
                  attachments={attachments}
                  isDragging={isDragging}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onFileChange={handleFileChange}
                  onRemoveFile={handleRemoveFile}
                  attachmentStatuses={attachmentStatuses}
                  currentReportId={currentReportId}
                  onRetryAttachment={retryAttachmentUpload}
                />
              )}
            </Suspense>

          {/* Mobile-first bottom fixed upload status panel - Progress only */}
          {(() => {
            if (employeeView !== "submit") return null;

            const hasAnyUploads = Object.keys(progressByAttachmentId).length > 0;
            if (!hasAnyUploads) return null;
            
            const globalProgress = computeGlobalProgress();

            const allFilesComplete =
              globalProgress.totalCount > 0 &&
              globalProgress.uploadedCount >= globalProgress.totalCount &&
              globalProgress.percent === 100;
            
            // Show progress bar as long as there are uploads and not all files are at 100%
            // OR if uploads are still actively in progress
            const isUploading = hasAnyUploads && (!allFilesComplete || hasActiveUploads);
            
            // Only show panel if uploading
            if (!isUploading) return null;
            
            // Show progress bar while uploading
            return (
              <div className="upload-status-panel">
                <div className="upload-status-panel-content">
                  <div className="upload-status-header">
                    <span className="upload-status-icon">📤</span>
                    <div className="upload-status-text">
                      <div className="upload-status-title">Μεταφόρτωση συνημμένων...</div>
                      <div className="upload-status-subtitle">
                        {globalProgress.uploadedCount} / {globalProgress.totalCount}
                      </div>
                    </div>
                    <div className="upload-status-percent">{globalProgress.percent}%</div>
                  </div>
                  <div className="upload-status-progress-bar">
                    <div 
                      className="upload-status-progress-fill" 
                      style={{ width: `${globalProgress.percent}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })()}
          <UploadSyncFooter />
          </div>
        </div>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </RoutingErrorBoundary>
        </AuthRoutingProvider>
      )}
      <AutoDismissToast
        show={showToast}
        message={errors.general}
        onClose={() => setShowToast(false)}
        delay={lockoutEndsAtMs ? 60 * 60 * 1000 : 3000}
      />
      <SubmissionSuccessModal
        show={showSubmissionSuccessModal}
        pendingFileCount={submissionSuccessPendingCount}
        onHide={() => {
          setShowSubmissionSuccessModal(false);
          setSubmissionSuccessPendingCount(0);
        }}
      />
      
      {/* Logout Confirmation Modal */}
      <Modal show={showLogoutConfirm} onHide={() => setShowLogoutConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Επιβεβαίωση Αποσύνδεσης</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {logoutConfirmReason === "upload_in_progress" ? (
            <p>
              Μεταφόρτωση σε εξέλιξη. Η αποσύνδεση θα ακυρώσει την αποστολή των αρχείων. Είστε
              σίγουροι;
            </p>
          ) : logoutConfirmReason === "pending_offline" ? (
            <p>
              Έχετε εκκρεμείς αναφορές εκτός σύνδεσης. Πρέπει να παραμείνετε συνδεδεμένοι για να
              αποσταλούν μόλις βρεθεί δίκτυο. Θέλετε σίγουρα να αποσυνδεθείτε;
            </p>
          ) : logoutConfirmReason === "unsaved_draft" ? (
            <p>
              Έχετε μια μισοτελειωμένη αναφορά. Η αποσύνδεση θα διαγράψει το προσχέδιο. Συνέχεια;
            </p>
          ) : (
            <p>Είστε σίγουροι ότι θέλετε να αποσυνδεθείτε;</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogoutConfirm(false)} autoFocus>
            Ακύρωση
          </Button>
          <Button variant="primary" onClick={() => performLogout()}>
            {logoutConfirmReason === "pending_offline"
              ? "Αποσύνδεση ούτως ή άλλως"
              : "Αποσύνδεση"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    </UploadJobsProvider>
  );
};

function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const m = String(error.message || "");
  return (
    /Loading chunk [\d]+ failed/i.test(m) ||
    /Failed to fetch dynamically imported module/i.test(m) ||
    /Importing a module script failed/i.test(m)
  );
}

/** Reloads the app when a lazy-loaded chunk is missing (e.g. after deploy). */
export class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { chunkFailed: false };
  }

  static getDerivedStateFromError(error) {
    return isChunkLoadError(error) ? { chunkFailed: true } : null;
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      window.location.reload();
    }
  }

  render() {
    if (this.state.chunkFailed) {
      return (
        <div className="chunk-load-reload d-flex align-items-center justify-content-center min-vh-100 px-3">
          <p className="text-muted text-center mb-0 small">
            Φόρτωση ενημερωμένης έκδοσης…
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AppWithChunkBoundary() {
  return (
    <BrowserRouter>
      <ChunkLoadErrorBoundary>
        <App />
      </ChunkLoadErrorBoundary>
    </BrowserRouter>
  );
}